import { AccessTimeOutlined, CalendarMonthOutlined, GolfCourseOutlined } from '@mui/icons-material';
import { Fade, Stack, Typography } from '@mui/material';
import React, { FC, MouseEvent } from 'react';
import { isToday } from 'date-fns';

import { AppointmentError } from 'application/modules/bookingWizard/useCases/hooks/useCaseChooseATimeForLesson';
import BookingCalendar from 'infrastructure/components/BookingCalendar';
import ButtonsGroup from 'infrastructure/components/ButtonsGroup';
import Loader from 'infrastructure/components/Loader';
import SectionTitle from 'infrastructure/components/SectionTitle';
import { useAppSelector } from 'infrastructure/redux/store/hooks';
import { selectLocationDetails } from 'infrastructure/redux/slices/bookingWizard.selector';
import { formatPhoneNumber } from 'infrastructure/targets/web/modules/common/helpers';
import LessonTypesButtonsGroup, {
  IButtonGroupButton,
} from 'infrastructure/targets/web/modules/bookingWizard/Steps/ChooseATimeStep/components/LessonTypesButtonsGroup';

import { StyledButtonsContainer, StyledCalendarContainer, StyledLoaderContainer } from './styles';

interface IBookLessonWithInstructor {
  showCalendar: boolean;
  inProgress: boolean;
  availableDates: string[] | undefined;
  reservationDate: string | undefined;
  handleDateChange: (value: Date | null) => void;
  fetchFurtherAvailableDates: (newDate: Date | null) => void;
  isFirstTimeLesson: boolean;
  lessonTypes: IButtonGroupButton[] | undefined;
  handleLessonTypeChange: (value: string, event: MouseEvent<HTMLElement>) => Promise<void>;
  lessonType: number | undefined;
  showStartTime: boolean;
  showLessonTypes: boolean;
  availableFirstTimeLessonSlots: IButtonGroupButton[] | undefined;
  availableLessonSlots: () =>
    | {
        value: string;
        title: string;
        secondaryValue: number | undefined;
      }[]
    | undefined;
  sessionHour: string;
  appointmentError: AppointmentError | undefined;
  areAppointmentsLoading: boolean;
  handleSessionHourChange: (value: string, event: MouseEvent<HTMLElement>) => Promise<void>;
  hasAvailableSlotsOnSelectedDate: boolean;
}
const BookLessonWithInstructor: FC<IBookLessonWithInstructor> = ({
  showCalendar,
  inProgress,
  availableDates,
  reservationDate,
  handleDateChange,
  fetchFurtherAvailableDates,
  isFirstTimeLesson,
  lessonTypes,
  handleLessonTypeChange,
  lessonType,
  showStartTime,
  showLessonTypes,
  availableFirstTimeLessonSlots,
  availableLessonSlots,
  sessionHour,
  appointmentError,
  areAppointmentsLoading,
  handleSessionHourChange,
  hasAvailableSlotsOnSelectedDate,
}) => {
  const selectedLocationDetails = useAppSelector(selectLocationDetails);
  const phoneNumber = selectedLocationDetails?.telephone;
  const availableLessonSlotsList = isFirstTimeLesson
    ? availableFirstTimeLessonSlots
    : availableLessonSlots();
  const noSlotsAvailable =
    !hasAvailableSlotsOnSelectedDate && !inProgress && !areAppointmentsLoading;
  const today = reservationDate ? isToday(new Date(reservationDate)) : false;

  return (
    <>
      {showCalendar && (
        <>
          <SectionTitle
            title={'Select Date'}
            subtitle={'Choose a date for your session. This can always be modified later.'}
            icon={<CalendarMonthOutlined />}
          />
          <StyledCalendarContainer>
            <Fade in={inProgress} timeout={400}>
              <StyledLoaderContainer>
                <Loader />
                <Typography variant="body2" color="text.secondary">
                  Fetching available days
                </Typography>
              </StyledLoaderContainer>
            </Fade>
            <BookingCalendar
              availableDates={availableDates?.map((item) => new Date(item))}
              selectedValue={reservationDate}
              onChange={handleDateChange}
              onMonthChange={fetchFurtherAvailableDates}
              rental={false}
            />
          </StyledCalendarContainer>

          {today ? (
            <>
              <SectionTitle title={'Select Lesson Type & Duration'} icon={<GolfCourseOutlined />} />
              <Stack
                sx={{
                  my: 6,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  Same-day bookings are not available online. Call {formatPhoneNumber(phoneNumber)}{' '}
                  to book a same day lesson or to get more information. Please use the date selector
                  for all future date availability.
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              {!isFirstTimeLesson && (
                <>
                  <SectionTitle
                    title={'Select Lesson Type & Duration'}
                    subtitle={
                      !showLessonTypes && hasAvailableSlotsOnSelectedDate
                        ? 'Select a date to view available lesson types.'
                        : undefined
                    }
                    icon={<GolfCourseOutlined />}
                  />
                  {noSlotsAvailable && (
                    <Stack
                      sx={{
                        my: 6,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Typography variant="h6" sx={{ textAlign: 'center' }}>
                        Lesson with this 5i coach on this date is currently unavailable.
                      </Typography>
                      <Typography variant="h6" sx={{ textAlign: 'center' }}>
                        Please select another date.
                      </Typography>
                    </Stack>
                  )}
                  {showLessonTypes && (
                    <StyledButtonsContainer>
                      <LessonTypesButtonsGroup
                        buttonList={lessonTypes}
                        onChange={handleLessonTypeChange}
                        selectedValue={lessonType}
                        infoCardVariant
                        shouldStayInOneRow
                      />
                    </StyledButtonsContainer>
                  )}
                </>
              )}
              {isFirstTimeLesson &&
                (!availableLessonSlotsList || availableLessonSlotsList.length === 0) && (
                  <SectionTitle
                    title={'Start Time'}
                    subtitle="There are currently no times available for this lesson date. Please select another date."
                    icon={<AccessTimeOutlined />}
                  />
                )}
              {areAppointmentsLoading && (
                <StyledLoaderContainer>
                  <Loader />
                  <Typography variant="body2">Fetching available appointments</Typography>
                </StyledLoaderContainer>
              )}

              {!areAppointmentsLoading && hasAvailableSlotsOnSelectedDate && (
                <>
                  <SectionTitle
                    title={'Start Time'}
                    subtitle={
                      !showStartTime && !isFirstTimeLesson
                        ? 'Select a lesson type to view available start times.'
                        : undefined
                    }
                    icon={<AccessTimeOutlined />}
                  />

                  {(showStartTime || isFirstTimeLesson) && (
                    <>
                      <StyledButtonsContainer>
                        {appointmentError && !areAppointmentsLoading && (
                          <StyledLoaderContainer>
                            <Typography variant="body2">
                              Sorry, but there was an error while fetching available apointments,
                              please try again later
                            </Typography>
                          </StyledLoaderContainer>
                        )}

                        {inProgress || (areAppointmentsLoading && !isFirstTimeLesson) ? (
                          <StyledLoaderContainer>
                            <Loader />
                            <Typography variant="body2">Fetching available appointments</Typography>
                          </StyledLoaderContainer>
                        ) : availableLessonSlotsList && availableLessonSlotsList.length > 0 ? (
                          <ButtonsGroup
                            buttonList={availableLessonSlotsList}
                            selectedValue={sessionHour}
                            onChange={handleSessionHourChange}
                          />
                        ) : (
                          <Typography variant="body2">
                            There are currently no times available for this lesson type. Please
                            select another date or choose a different lesson type.
                          </Typography>
                        )}
                      </StyledButtonsContainer>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default BookLessonWithInstructor;
